/* import __COLOCATED_TEMPLATE__ from './mention-typeahead.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { debounce } from '@ember/runloop';
import type Composer from '@intercom/composer-ember/lib/composer';
import { type TypeaheadMatchResult } from '@intercom/composer-ember';
import type AdminWithPermissions from 'embercom/objects/inbox/admin-with-permissions';
import type Session from 'embercom/services/session';
import { AdminSearchContexts } from 'embercom/objects/inbox/macro';
import type Inbox2AssigneeSearch from 'embercom/services/inbox2-assignee-search';
import { EntityType } from 'embercom/models/data/entity-types';
import type SlackUsersService from 'embercom/services/slack-users-service';
import type { SlackUser } from 'embercom/services/slack-users-service';
import type DiscordUsersService from 'embercom/services/discord-users-service';
import type { DiscordUser } from 'embercom/services/discord-users-service';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  composer: Composer;
  matchResult: TypeaheadMatchResult | null;
  registerDelegate(f: Function): void;
  deregisterDelegate(f: Function): void;
  attrs?: { conversation: any; mentionType?: string };
}

interface Signature {
  Args: Args;
}

interface ExternalChannelUserMentionData {
  id: string;
  name: string;
  displayName?: string;
  avatarUrl: string | null;
  inChannel?: boolean;
  isExternal?: boolean;
  type: 'user' | 'usergroup';
}

type MentionItem =
  | { data: AdminWithPermissions; entityType: EntityType.Admin | EntityType.Team }
  | {
      data: ExternalChannelUserMentionData;
      entityType: EntityType.SlackUser | EntityType.DiscordPrivateThread;
    };

const MAX_MENTION_SEARCH_LENGTH = 50;
const MAX_MENTION_NUM_SPACES = 2;
const MAX_MENTIONS_RESULTS = 8;

export default class MentionTypeahead extends Component<Signature> {
  @service declare session: Session;
  @service declare inbox2AssigneeSearch: Inbox2AssigneeSearch;
  @service declare slackUsersService: SlackUsersService;
  @service declare discordUsersService: DiscordUsersService;
  @service declare intl: IntlService;
  @service regionService: any;
  @service intercomEventService: any;

  @tracked slackUsers: SlackUser[] = [];
  @tracked discordUsers: DiscordUser[] = [];
  @tracked discordSearchResults: DiscordUser[] = [];
  @tracked isSearchingDiscord = false;
  private lastDiscordSearchTerm = '';
  private searchAbortController: AbortController | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (this.isSlackMention) {
      this.loadSlackUsers();
    }
    if (this.isDiscordMention) {
      this.loadDiscordUsers();
    }
  }

  get isLoadingSlackUsers() {
    return this.slackUsersService.isLoading;
  }

  get isLoadingDiscordUsers() {
    return this.discordUsersService.isLoading;
  }

  get showDiscordSearchLoading(): boolean {
    return this.isDiscordMention && this.isSearchingDiscord;
  }

  get itemsWidth(): number | undefined {
    return this.isChannelMention ? 500 : undefined;
  }

  get isSlackMention(): boolean {
    return this.args.attrs?.mentionType === 'slack';
  }

  get isDiscordMention(): boolean {
    return this.args.attrs?.mentionType === 'discord';
  }

  get isChannelMention(): boolean {
    return this.isSlackMention || this.isDiscordMention;
  }

  get channelMentionTitle(): string | undefined {
    if (this.isSlackMention) {
      return this.intl.t('inbox.composer.slack.mention.title');
    }
    if (this.isDiscordMention) {
      return this.intl.t('inbox.composer.discord.mention.title');
    }
    return undefined;
  }

  get channelMentionIcon(): string | undefined {
    if (this.isSlackMention) {
      return 'slack';
    }
    if (this.isDiscordMention) {
      return 'discord';
    }
    return undefined;
  }

  get slackChannelId(): string | null {
    if (!this.args.attrs?.conversation) {
      return null;
    }

    let conversation = this.args.attrs.conversation;

    // Check linkedChannel first for side conversations
    if (conversation.linkedChannel?.channel_id) {
      return conversation.linkedChannel.channel_id;
    }

    let slackChannelAttribute = conversation.attributes?.find(
      (attr: any) => attr.descriptor?.id === 'slack_channel',
    );
    if (!slackChannelAttribute) {
      slackChannelAttribute = conversation.attributes?.find(
        (attr: any) => attr.descriptor?.id === 'slack_dm_channel',
      );
    }
    if (typeof slackChannelAttribute?.value !== 'number') {
      return null;
    }

    return slackChannelAttribute.value.toString();
  }

  async loadSlackUsers() {
    let channelId = this.slackChannelId;
    if (!channelId) {
      return;
    }

    try {
      this.slackUsers = await this.slackUsersService.fetchSlackChannelMembers(channelId);
    } catch (error) {
      console.error('[MentionTypeahead] Error loading Slack channel members:', error);
      this.slackUsers = [];
    }
  }

  get discordChannelId(): string | null {
    if (!this.args.attrs?.conversation) {
      return null;
    }

    let conversation = this.args.attrs.conversation;
    let discordChannelAttribute = conversation.attributes?.find(
      (attr: any) => attr.descriptor?.id === 'discord_channel',
    );
    if (!discordChannelAttribute?.value) {
      return null;
    }

    return discordChannelAttribute.value.toString();
  }

  async loadDiscordUsers() {
    let channelId = this.discordChannelId;
    if (!channelId) {
      return;
    }

    try {
      this.discordUsers = await this.discordUsersService.fetchDiscordMembers(channelId);
    } catch (e: unknown) {
      console.error('[MentionTypeahead] Error loading Discord members:', e);
      this.discordUsers = [];
    }
  }

  get isPrivateChannel(): boolean {
    return this.slackUsersService.isPrivateChannel(this.slackChannelId);
  }

  get query() {
    return this.args.matchResult?.text || '';
  }

  get items() {
    if (this.query.search('@') === -1 || this.query === '@ ') {
      return [];
    }

    let searchTerm = this.query.replace('@', '');
    let numSpaces = searchTerm.split(' ').length - 1;

    if (searchTerm.length > MAX_MENTION_SEARCH_LENGTH || numSpaces > MAX_MENTION_NUM_SPACES) {
      return [];
    }

    if (this.isSlackMention) {
      return this.getSlackUserItems(searchTerm);
    }

    if (this.isDiscordMention) {
      return this.getDiscordUserItems(searchTerm);
    }

    return this.inbox2AssigneeSearch.filterAdminsAndTeams(
      searchTerm,
      AdminSearchContexts.Mentions,
      MAX_MENTIONS_RESULTS,
    ).results;
  }

  private getDiscordUserItems(searchTerm: string) {
    if (searchTerm) {
      if (searchTerm !== this.lastDiscordSearchTerm) {
        this.lastDiscordSearchTerm = searchTerm;
        debounce(this, this.performDiscordSearch, 300);
      }
      return this.discordSearchResults.slice(0, MAX_MENTIONS_RESULTS).map((user) => ({
        data: {
          id: user.id,
          name: user.username,
          displayName: user.display_name,
          avatarUrl: user.avatar_url,
          type: user.type,
        },
        entityType: EntityType.DiscordPrivateThread,
      }));
    }

    this.lastDiscordSearchTerm = '';
    this.discordSearchResults = [];
    if (this.isLoadingDiscordUsers) {
      return [];
    }
    return this.discordUsers.slice(0, MAX_MENTIONS_RESULTS).map((user) => ({
      data: {
        id: user.id,
        name: user.username,
        displayName: user.display_name,
        avatarUrl: user.avatar_url,
        type: user.type,
      },
      entityType: EntityType.DiscordPrivateThread,
    }));
  }

  private async performDiscordSearch() {
    let channelId = this.discordChannelId;
    let searchTerm = this.lastDiscordSearchTerm;
    if (!channelId || !searchTerm) {
      return;
    }

    this.searchAbortController?.abort();
    let controller = new AbortController();
    this.searchAbortController = controller;

    try {
      this.isSearchingDiscord = true;
      let results = await this.discordUsersService.searchDiscordMembers(
        channelId,
        searchTerm,
        controller.signal,
      );
      this.discordSearchResults = results;
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        return;
      }
      console.error('[MentionTypeahead] Discord search error:', e);
    } finally {
      if (this.searchAbortController === controller) {
        this.isSearchingDiscord = false;
      }
    }
  }

  private getSlackUserItems(searchTerm: string) {
    if (this.isLoadingSlackUsers) {
      return [];
    }

    let filteredUsers = this.slackUsersService.filterUsers(this.slackUsers, searchTerm);
    return filteredUsers.slice(0, MAX_MENTIONS_RESULTS).map((user) => ({
      data: {
        id: user.id,
        name: user.name,
        displayName: user.real_name,
        avatarUrl: user.avatar_url,
        inChannel: user.in_channel,
        isExternal: user.is_external,
        type: user.type,
      },
      entityType: EntityType.SlackUser,
    }));
  }

  @action
  insertMention(selectedItem: MentionItem) {
    if (!this.args.matchResult) {
      return;
    }

    let prosemirrorState = this.args.composer.state.prosemirrorState;

    if (prosemirrorState) {
      let { tr } = prosemirrorState;
      let mentionSpec = prosemirrorState.schema.nodes.mention;
      let typeOfMention = 'admin';
      if (selectedItem.entityType === EntityType.Team) {
        typeOfMention = 'team';
      } else if (selectedItem.entityType === EntityType.SlackUser) {
        typeOfMention = 'slack_user';
      } else if (selectedItem.entityType === EntityType.DiscordPrivateThread) {
        typeOfMention = 'discord_user';
      }

      this.intercomEventService.trackAnalyticsEvent({
        action: 'created',
        object: `${typeOfMention}_mention`,
        place: 'inbox',
      });
      let name = selectedItem.data.name;

      let uri = `//${this.regionService.appHost}/apps/${this.session.workspace.id}/${typeOfMention}/${selectedItem.data.id}`;
      let mentionType = this.args.attrs?.mentionType;
      if (
        (selectedItem.entityType === EntityType.SlackUser ||
          selectedItem.entityType === EntityType.DiscordPrivateThread) &&
        mentionType
      ) {
        uri = `${mentionType}://${selectedItem.data.type}/${selectedItem.data.id}`;
        name = `@${name}`;
      }

      let node = mentionSpec.createAndFill({
        name,
        uri,
      });

      tr.delete(this.args.matchResult.range.from, this.args.matchResult.range.to);
      if (node) {
        tr.insert(this.args.matchResult.range.from, node);
      }
      this.args.composer.commands.dispatch(tr);
      this.args.composer.commands.insertText(' ');
      this.args.composer.commands.focus();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Composer::MentionTypeahead': typeof MentionTypeahead;
    'inbox2/composer/mention-typeahead': typeof MentionTypeahead;
  }
}
